import React, { useMemo, useState } from 'react'
import { Box, Text } from 'front-end/src/components'
import { SelectEntitiesFormViewSearch } from 'front-end/src/components/common/selectEntitiesForm/SelectEntitiesFormViewSearch'
import { Trans } from '@lingui/macro'
import { InfiniteScrollWrapper } from 'front-end/src/components/utils/InfiniteScrollWrapper'
import { mapEntitiesToSearchableItem } from 'front-end/src/components/common/selectEntitiesForm/utils'
import { useDebouncedState } from 'front-end/src/app/hooks/useDebouncedState'
import { EventSelectorRowItem } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/event-modal/event-selector/EventSelectorRowItem'
import {
  getAdditionalEvents,
  getSearchableOccurrences,
  getSortedEvents,
  getSortedRecEvents,
  getSuggestedItemsWithoutOccurrences
} from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/event-modal/event-selector/helpers'
import {
  MatchedSearchableItem,
  SearchableEntity,
  SearchableItem
} from 'front-end/src/components/common/selectEntitiesForm'
import { useFilterSuggestions } from 'front-end/src/components/common/selectEntitiesForm/FuzzyResourcesFilter'
import { useEventSelectorContext } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/event-modal/event-selector/EventSelectorContext'

export const EventSelectorForm = ({
  allEvents
}: {
  allEvents: {
    events: SearchableEntity[]
    recEvents: SearchableEntity[]
  }
}) => {
  const { selectedEvents } = useEventSelectorContext()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedState(query, 100)
  const resetQuery = React.useCallback(() => {
    setQuery('')
  }, [setQuery])

  const searchableEvents = mapEntitiesToSearchableItem(allEvents.events)
  const searchableRecEvents = mapEntitiesToSearchableItem(allEvents.recEvents)
  const mergedEvents = useMemo(
    () => [...searchableEvents, ...searchableRecEvents],
    [allEvents]
  )

  const suggestedAllEvents = useFilterSuggestions(mergedEvents, debouncedQuery)

  const suggestedEvents = suggestedAllEvents.filter(
    (item) => item.item.type !== 'RecurringEventModelAsAdmin'
  )
  const suggestedRecEvents = suggestedAllEvents.filter(
    (item) => item.item.type === 'RecurringEventModelAsAdmin'
  )

  const additionalEvents = useMemo<
    MatchedSearchableItem<SearchableItem>[]
  >(() => {
    if (!debouncedQuery) {
      return []
    }
    return getAdditionalEvents(
      suggestedEvents,
      suggestedRecEvents,
      searchableRecEvents
    )
  }, [suggestedAllEvents])

  const suggestedAndAdditionalRecEvents = getSortedRecEvents(
    [...suggestedRecEvents, ...additionalEvents],
    selectedEvents
  )

  const suggestedEventsWithoutOccurrences = getSortedEvents(
    getSuggestedItemsWithoutOccurrences(suggestedEvents),
    selectedEvents.events
  )

  const checkedEvents = suggestedEventsWithoutOccurrences.filter((event) =>
    selectedEvents.events.includes(event.item.id)
  )

  const nonCheckedEvents = suggestedEventsWithoutOccurrences.filter(
    (event) => !selectedEvents.events.includes(event.item.id)
  )

  const connectedSuggestedEvents = useMemo(
    () => [
      ...checkedEvents,
      ...suggestedAndAdditionalRecEvents,
      ...nonCheckedEvents
    ],
    [suggestedAllEvents]
  )

  return (
    <>
      <SelectEntitiesFormViewSearch
        defaultQuery={query}
        resetQuery={resetQuery}
        changeQuery={setQuery}
      />

      <Box className="List">
        {connectedSuggestedEvents.length === 0 && (
          <Text className="List__Row__noResults">
            <Trans>No results</Trans>
          </Text>
        )}
        <InfiniteScrollWrapper items={connectedSuggestedEvents}>
          {(slicedItems) =>
            slicedItems.map((searchableItem: MatchedSearchableItem) => {
              if (searchableItem) {
                return (
                  <EventSelectorRowItem
                    key={`${searchableItem.item.type}-${
                      searchableItem.item.id
                    }-${Boolean(query).toString()}`}
                    matchedItem={searchableItem}
                    occurrences={getSearchableOccurrences(
                      searchableItem,
                      searchableEvents,
                      query
                    )}
                    defaultIsOpen={!!query}
                  />
                )
              }
            })
          }
        </InfiniteScrollWrapper>
      </Box>
    </>
  )
}
